import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import styles from './Slider.module.scss';
import SwiperCore, { Navigation, Pagination, Scrollbar, A11y } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SliderItem, SliderItemProps } from './SliderItem/SliderItem';
import { Button, ButtonProps, Icon } from 'ui/components';
import PrevSlideChevronIcon from 'ui/icons/icon-chevron-left-web.svg';
import NextSlideChevronIcon from 'ui/icons/icon-chevron-right-web.svg';

//? NOTE: This rule is disabled because SwiperCore is used in a way that requires it to be imported at the top level.
//? NOTE: This is a workaround for the Swiper library's requirement to use hooks at the top level.
// eslint-disable-next-line react-hooks/rules-of-hooks
SwiperCore.use([Navigation, Pagination, Scrollbar, A11y]);

export interface SliderProps {
	className?: string;
	navButtonsClassName?: string;
	slides: SliderItemProps[];
	dictionary?: { [key: string]: string };
	sliderAriaControls?: string;
	autoHeight?: boolean;
	sliderRef?: React.RefObject<HTMLDivElement>;
	showPagination?: boolean;
	slideStyleClass?: string;
	slideAspectRatio?: number;
	isLoop?: boolean;
}

export const Slider: React.FC<SliderProps> = ({
	className,
	navButtonsClassName,
	slides,
	dictionary,
	sliderAriaControls,
	autoHeight,
	showPagination = true,
	slideAspectRatio,
	sliderRef,
	slideStyleClass,
	isLoop = true,
}) => {
	const [swiperInstance, setSwiperInstance] = useState<SwiperCore | null>(null);
	const [activeSlideIndex, setActiveSlideIndex] = useState(swiperInstance?.realIndex || 0);
	const isSingleSlide = slides?.length === 1;

	const handlePrevClick = () => {
		if (swiperInstance) {
			swiperInstance.slidePrev();
		}
	};

	const handleNextClick = () => {
		if (swiperInstance) {
			swiperInstance.slideNext();
		}
	};

	const handleSlideChange = () => {
		if (swiperInstance) {
			setActiveSlideIndex(activeSlideIndex);
		}
	};

	useEffect(() => {
		const s = swiperInstance;
		if (!s) return;

		// Fast pass right away (keeps things snappy)
		s.update();
		if (autoHeight) s.updateAutoHeight(0);

		// Main correction after initial paint/content settle
		const timeoutId = setTimeout(() => {
			s.update();
			if (autoHeight) s.updateAutoHeight(0);
		}, 250);

		// Re-run when window fully loaded (images/scripts)
		const onLoad = () => {
			s.update();
			if (autoHeight) s.updateAutoHeight(0);
		};
		window.addEventListener('load', onLoad);

		// Re-run after web fonts render
		const fontsReady = (document as any).fonts?.ready;
		let fontsPromise: Promise<any> | undefined;
		if (fontsReady) {
			fontsPromise = (document as any).fonts.ready.then(() => {
				s.update();
				if (autoHeight) s.updateAutoHeight(0);
			});
		}

		return () => {
			clearTimeout(timeoutId);
			window.removeEventListener('load', onLoad);
		};
	}, [swiperInstance, autoHeight]);

	return (
		<div className={classNames(styles.Slider_container, className)} ref={sliderRef}>
			{!isSingleSlide && (
				<div className={classNames(styles.Slider_navButtonsContainer, navButtonsClassName)}>
					<Button
						className={classNames(styles.Slider_button, styles.Slider_buttonPrev)}
						{...(sliderAriaControls?.trim() ? { ariaControls: sliderAriaControls.trim() } : {})}
						ariaLabel={dictionary?.buttonPrev}
						onClick={handlePrevClick}
					>
						<Icon size="md" className={styles.Slider_buttonIcon}>
							<PrevSlideChevronIcon />
						</Icon>
					</Button>
					<Button
						className={classNames(styles.Slider_button, styles.Slider_buttonNext)}
						{...(sliderAriaControls?.trim() ? { ariaControls: sliderAriaControls.trim() } : {})}
						ariaLabel={dictionary?.buttonNext}
						onClick={handleNextClick}
					>
						<Icon size="md" className={styles.Slider_buttonIcon}>
							<NextSlideChevronIcon />
						</Icon>
					</Button>
				</div>
			)}
			<Swiper
				{...(sliderAriaControls?.trim() ? { id: sliderAriaControls.trim() } : {})}
				spaceBetween={50}
				slidesPerView={1}
				allowTouchMove={!isSingleSlide}
				pagination={
					isSingleSlide || !showPagination
						? false
						: {
								clickable: true,
								type: 'bullets',
						  }
				}
				lazy
				loop={!isSingleSlide && isLoop}
				onSwiper={setSwiperInstance}
				onSlideChange={handleSlideChange}
				onSlideChangeTransitionEnd={(s) => s.updateAutoHeight(250)}
				onInit={(s) => {
					s.update();
					if (autoHeight) s.updateAutoHeight(0);
				}}
				onImagesReady={(s) => {
					s.update();
					if (autoHeight) s.updateAutoHeight(0);
				}}
				className={styles.Slider_swiper}
				breakpoints={{
					// when window width is >= 320px
					320: {
						slidesPerView: 1,
						spaceBetween: 15,
					},
				}}
				autoHeight={autoHeight}
				observer
				observeParents
				observeSlideChildren
				updateOnWindowResize
				preloadImages
				style={
					{
						'--swiper-pagination-color': '#213555',
						'--swiper-pagination-bullet-inactive-color': '#BFC8D4',
						'--swiper-pagination-bullet-inactive-opacity': '1',
					} as React.CSSProperties & Record<string, string>
				}
			>
				{slides?.length > 0 &&
					slides.map((slide, index) => (
						<SwiperSlide key={index} className={classNames(styles.Slider_slide, slideStyleClass)}>
							<SliderItem {...slide} imageAspectRatio={slideAspectRatio} />
						</SwiperSlide>
					))}
			</Swiper>
		</div>
	);
};
